/**
 * Google Ads measurement only for verified Publisher Membership Purchase.
 * Must never affect payment, membership, or navigation outcomes.
 */

const GOOGLE_ADS_MEMBERSHIP_PURCHASE_SEND_TO =
  "AW-18407106828/VEW7CLTq9-YcEI2SmM1E";

const FIRED_KEY_PREFIX = "oac_gads_membership_purchase_fired:";
const AMOUNT_CTX_PREFIX = "oac_gads_membership_purchase_ctx:";

type PurchaseAmountContext = {
  amountPaise: number;
  currency: string;
};

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

function readStorage(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Privacy mode / blocked storage must not affect checkout.
  }
}

/**
 * Remember the checkout amount for a payment so Stripe return URLs
 * (which only carry paymentId) can still report the verified amount.
 */
export function rememberMembershipPurchaseCheckoutAmount(
  paymentId: string,
  amountPaise: number,
  currency: string,
): void {
  if (!paymentId || !Number.isFinite(amountPaise) || amountPaise <= 0) {
    return;
  }

  const payload: PurchaseAmountContext = {
    amountPaise,
    currency: (currency || "INR").toUpperCase(),
  };
  writeStorage(AMOUNT_CTX_PREFIX + paymentId, JSON.stringify(payload));
}

function resolveAmount(
  paymentId: string,
  amountPaise?: number,
  currency?: string,
): PurchaseAmountContext | null {
  if (typeof amountPaise === "number" && Number.isFinite(amountPaise) && amountPaise > 0) {
    return {
      amountPaise,
      currency: (currency || "INR").toUpperCase(),
    };
  }

  const raw = readStorage(AMOUNT_CTX_PREFIX + paymentId);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as PurchaseAmountContext;
    if (
      typeof parsed.amountPaise === "number" &&
      Number.isFinite(parsed.amountPaise) &&
      parsed.amountPaise > 0
    ) {
      return {
        amountPaise: parsed.amountPaise,
        currency: (parsed.currency || "INR").toUpperCase(),
      };
    }
  } catch {
    return null;
  }

  return null;
}

/**
 * Observe a verified OpenAppCart payment success and emit one Google Ads conversion.
 * Safe to call repeatedly for the same paymentId within a browser session.
 */
export function reportPublisherMembershipPurchaseConversion(input: {
  paymentId: string;
  amountPaise?: number;
  currency?: string;
}): void {
  try {
    const paymentId = input.paymentId?.trim();
    if (!paymentId) {
      return;
    }

    const firedKey = FIRED_KEY_PREFIX + paymentId;
    if (readStorage(firedKey) === "1") {
      return;
    }

    const amount = resolveAmount(paymentId, input.amountPaise, input.currency);
    if (!amount) {
      return;
    }

    // Mark before gtag so polling / StrictMode remounts cannot double-fire.
    writeStorage(firedKey, "1");

    if (typeof window.gtag !== "function") {
      return;
    }

    window.gtag("event", "conversion", {
      send_to: GOOGLE_ADS_MEMBERSHIP_PURCHASE_SEND_TO,
      value: amount.amountPaise / 100,
      currency: amount.currency || "INR",
      transaction_id: paymentId,
    });
  } catch {
    // Tracking must never affect commercial flow.
  }
}
